import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Loader2, Heart } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { ResourceCard } from "@/components/ResourceCard";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATS = ["Toutes", "BTS", "CAP", "BP"];

const KINDS = [
  { key: "", label: "Tous formats" },
  { key: "pdf", label: "PDF" },
  { key: "video", label: "Vidéo" },
  { key: "audio", label: "Audio" },
  { key: "image", label: "Image" },
  { key: "html", label: "HTML" },
  { key: "document", label: "Document" },
];

export default function Resources() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "Toutes");
  const [kind, setKind] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favOnly, setFavOnly] = useState(false);
  const [module, setModule] = useState("");
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [notify, setNotify] = useState(user?.notify_new_resources !== false);

  const loadProgress = useCallback(() => {
    api.get("/progress/summary").then(({ data }) => setProgress(data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  useEffect(() => {
    setModule("");
    api
      .get("/modules", { params: { category: category !== "Toutes" ? category : undefined } })
      .then(({ data }) => setModules(data))
      .catch(() => setModules([]));
  }, [category]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/resources", {
        params: {
          q: query || undefined,
          category: category !== "Toutes" ? category : undefined,
          kind: kind || undefined,
          favorites: favOnly || undefined,
          module: module || undefined,
        },
      });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [query, category, kind, favOnly, module]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
  }, [fetchItems]);

  useEffect(() => {
    const next = {};
    if (query) next.q = query;
    if (category !== "Toutes") next.category = category;
    setParams(next, { replace: true });
  }, [query, category, setParams]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <p className="text-xs uppercase tracking-[0.3em] text-[#B76E79] mb-4">Bibliothèque</p>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight text-[#2C2627] mb-4">
          Bonjour {user?.name?.split(" ")[0] || ""}, voici vos ressources
        </h1>
        <p className="text-sm text-[#5E5356] max-w-xl mb-12 leading-relaxed">
          Filtrez par rubrique ou par format, puis téléchargez les supports de cours dont vous avez besoin.
        </p>

        {progress && progress.total > 0 && (
          <div
            data-testid="progress-panel"
            className="bg-white border border-[#E8D7D0] rounded-3xl p-8 lg:p-10 mb-14"
          >
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-[#B76E79] mb-2">Ma progression</p>
                <h2 className="font-display text-2xl lg:text-3xl text-[#2C2627]">
                  {progress.viewed} support{progress.viewed > 1 ? "s" : ""} consulté
                  {progress.viewed > 1 ? "s" : ""} sur {progress.total}
                </h2>
              </div>
              <p className="text-sm text-[#5E5356]" data-testid="progress-remaining">
                Il vous reste {progress.remaining} support{progress.remaining > 1 ? "s" : ""} à découvrir
              </p>
            </div>

            <label className="flex items-center gap-3 mb-8 cursor-pointer">
              <input
                data-testid="notify-toggle"
                type="checkbox"
                checked={notify}
                onChange={async (e) => {
                  const next = e.target.checked;
                  setNotify(next);
                  try {
                    await api.patch("/me/notifications", { notify_new_resources: next });
                    toast.success(next ? "Alertes email activées" : "Alertes email désactivées");
                  } catch {
                    setNotify(!next);
                    toast.error("Action impossible");
                  }
                }}
                className="w-4 h-4 accent-[#B76E79]"
              />
              <span className="text-sm text-[#5E5356]">
                Me prévenir par email des nouveaux supports de ma filière
              </span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {["BTS", "CAP", "BP"].map((c) => {
                const s = progress.categories[c];
                const pct = s.total ? Math.round((s.viewed / s.total) * 100) : 0;
                return (
                  <div key={c} data-testid={`progress-${c}`}>
                    <div className="flex items-baseline justify-between mb-3">
                      <span className="text-xs uppercase tracking-[0.2em] text-[#2C2627]">{c}</span>
                      <span className="text-xs text-[#5E5356]">
                        {s.viewed}/{s.total}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-[#F0E4E0] overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#B76E79] transition-[width] duration-700 ease-out"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-6 mb-14">
          <div className="relative max-w-2xl">
            <Search strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#B76E79]" />
            <Input
              data-testid="resources-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un support…"
              className="h-14 pl-12 rounded-full bg-white border-[#E8D7D0] focus-visible:ring-[#B76E79] text-base"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`filter-category-${c}`}
                onClick={() => setCategory(c)}
                className={`h-10 px-6 rounded-full text-sm font-medium border transition-colors duration-300 ${
                  category === c
                    ? "bg-[#2C2627] text-white border-[#2C2627]"
                    : "bg-white text-[#5E5356] border-[#E8D7D0] hover:bg-[#F5E6E8]"
                }`}
              >
                {c}
              </button>
            ))}
            <button
              data-testid="filter-favorites"
              onClick={() => setFavOnly((v) => !v)}
              className={`h-10 px-6 rounded-full text-sm font-medium border inline-flex items-center gap-2 transition-colors duration-300 ${
                favOnly
                  ? "bg-[#B76E79] text-white border-[#B76E79]"
                  : "bg-white text-[#5E5356] border-[#E8D7D0] hover:bg-[#F5E6E8]"
              }`}
            >
              <Heart strokeWidth={1.5} className={`w-4 h-4 ${favOnly ? "fill-white" : ""}`} />
              Mes favoris
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {KINDS.map((k) => (
              <button
                key={k.key || "all"}
                data-testid={`filter-kind-${k.key || "all"}`}
                onClick={() => setKind(k.key)}
                className={`h-9 px-4 rounded-full text-xs uppercase tracking-[0.14em] border transition-colors duration-300 ${
                  kind === k.key
                    ? "bg-[#B76E79] text-white border-[#B76E79]"
                    : "bg-transparent text-[#5E5356] border-[#E8D7D0] hover:bg-[#F5E6E8]"
                }`}
              >
                {k.label}
              </button>
            ))}
          </div>

          {modules.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="module-filters">
              <span className="text-xs uppercase tracking-[0.2em] text-[#5E5356] mr-2">Modules</span>
              <button
                data-testid="filter-module-all"
                onClick={() => setModule("")}
                className={`h-9 px-4 rounded-full text-xs border transition-colors duration-300 ${
                  module === ""
                    ? "bg-[#2C2627] text-white border-[#2C2627]"
                    : "bg-white text-[#5E5356] border-[#E8D7D0] hover:bg-[#F5E6E8]"
                }`}
              >
                Tous
              </button>
              {modules.map((m) => (
                <button
                  key={m}
                  data-testid={`filter-module-${m}`}
                  onClick={() => setModule(m)}
                  className={`h-9 px-4 rounded-full text-xs border transition-colors duration-300 ${
                    module === m
                      ? "bg-[#2C2627] text-white border-[#2C2627]"
                      : "bg-white text-[#5E5356] border-[#E8D7D0] hover:bg-[#F5E6E8]"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-[#B76E79]" data-testid="resources-loading">
            <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} />
            <span className="font-display text-xl">Chargement…</span>
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="resources-empty"
            className="border border-dashed border-[#E8D7D0] rounded-3xl p-14 bg-white/60 max-w-2xl"
          >
            <h2 className="font-display text-2xl text-[#2C2627] mb-3">Aucune ressource trouvée</h2>
            <p className="text-sm text-[#5E5356]">
              Modifiez votre recherche ou vos filtres. De nouveaux supports sont ajoutés régulièrement.
            </p>
          </div>
        ) : (
          <div data-testid="resources-grid" className="space-y-14">
            {Object.entries(
              items.reduce((acc, r) => {
                const key = r.subcategory || "Sans module";
                (acc[key] = acc[key] || []).push(r);
                return acc;
              }, {})
            ).map(([moduleName, list]) => (
              <section key={moduleName} data-testid={`module-section-${moduleName}`}>
                <div className="flex items-baseline gap-4 mb-7">
                  <h2 className="font-display text-2xl lg:text-3xl text-[#2C2627]">{moduleName}</h2>
                  <span className="text-xs uppercase tracking-[0.2em] text-[#5E5356]">
                    {list.length} support{list.length > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                  {list.map((r, i) => (
                    <ResourceCard
                      key={r.id}
                      resource={r}
                      index={i}
                      isAdmin={user?.role === "admin"}
                      onDeleted={(id) => setItems((prev) => prev.filter((x) => x.id !== id))}
                      onFavoriteChange={(id, next) => {
                        if (favOnly && !next) setItems((prev) => prev.filter((x) => x.id !== id));
                      }}
                      onViewed={loadProgress}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
